import React, { useState } from 'react';
import { absoluteURL, pageTypeOf } from './ProposalQueue';

// Confirming before a company is switched on.
//
// The monitoring switch used to act immediately, which asked the user to make two
// judgements it gave them nothing for: is this company worth watching, and is there
// anything to watch it THROUGH.
//
// The second is the one that bites. Switching on a company with no website and no
// LinkedIn monitors a company that watches NOTHING — the exact dead end the proposal
// queue was redesigned to remove, reachable here by another route. So this dialog
// enforces the same rule the queue does: no page, no watching, and the page is
// asked for in place rather than left as a task for later.

export default function StartWatchingDialog({
  company, onConfirm, onSupplyPage, onCancel, loading = false, error = '', busy = false,
}) {
  const [url, setUrl] = useState('');

  // has_page, NOT suggested_url. The suggestion is empty for two different reasons —
  // we hold nothing, or we hold it and it is already a source — and only the first
  // means the user must supply one. Reading the blank alone would demand a URL for a
  // company whose website is already being watched.
  const needsPage = Boolean(company) && !company.has_page;
  const ready = Boolean(company) && (!needsPage || absoluteURL(url) !== '');

  const start = () => {
    if (!ready) return;
    if (needsPage) {
      // Supplying the page IS the act of starting to watch: one request that writes
      // the source, the company's website column and signals_on together. Confirming
      // first and adding the page after is the two-step flow this replaces.
      const v = absoluteURL(url);
      onSupplyPage(company, v, pageTypeOf(v));
      return;
    }
    onConfirm(company);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-6"
      role="dialog" aria-modal="true" aria-label="Start watching this company">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl overflow-hidden">
        <header className="px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">
            {company ? `Start watching ${company.name}?` : 'Start watching'}
          </h3>
        </header>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{error}</p>
          )}
          {loading && <p className="text-sm text-gray-500">Loading this company…</p>}

          {company && (
            <>
              {company.reasons && company.reasons.length > 0 ? (
                <ul className="space-y-1">
                  {company.reasons.map((r) => (
                    <li key={r.code} className="text-sm text-gray-700 flex gap-2 items-start">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00C6AE] shrink-0" aria-hidden="true" />
                      <span>{r.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                // The absence is stated, not left blank: it means the company is
                // quiet, which is a fact worth knowing before committing attention.
                <p className="text-sm text-gray-500">
                  Nothing on your books for this company yet — no roles, contacts or
                  recorded contact. Watching it is a bet on what comes next.
                </p>
              )}

              {needsPage ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <p className="text-[12.5px] text-amber-800">
                    No website or LinkedIn on record, so there is nothing to watch them
                    through. Add a page and we will follow it from here.
                  </p>
                  <input
                    id="sw-url"
                    type="url"
                    value={url}
                    disabled={busy}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); start(); } }}
                    aria-label={`Page URL for ${company.name}`}
                    placeholder="Paste their website or LinkedIn…"
                    className="mt-2 w-full font-mono text-[13px] px-2.5 py-1.5 rounded-lg border border-amber-300 bg-white focus:outline-none focus:border-[#00C6AE]"
                  />
                </div>
              ) : (
                company.suggested_url && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-1">
                      We will watch
                    </p>
                    <p className="font-mono text-[12.5px] text-gray-700 break-all">
                      {company.suggested_url}
                    </p>
                  </div>
                )
              )}
            </>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-gray-200 text-gray-600">
            Cancel
          </button>
          {company && (
            <button type="button" disabled={!ready || busy} onClick={start}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-[#00C6AE] text-white hover:bg-[#00b09b] disabled:opacity-50">
              Start watching
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
